import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type Auth,
  type UserCredential,
} from "firebase/auth";

// Firebase requires us to remember which email the user typed when they
// requested the magic link, so when they click it (potentially on a
// different device/tab) we can complete the sign-in. Stored in localStorage
// under this key. Cleared on completion.
const PENDING_EMAIL_KEY = "formular:auth:pendingEmail";

// Path inside the SPA where the magic link returns. Lives at the public
// hosting origin so Firebase auto-authorizes it. The route handler is in
// pages/auth/EmailLinkComplete.tsx.
const COMPLETE_PATH = "/auth/email-link-complete";

export async function sendMagicLink(
  auth: Auth,
  email: string,
  options: { continueTo?: string } = {},
): Promise<void> {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed || !trimmed.includes("@")) {
    throw new Error("Introduce un email válido.");
  }
  // `continueTo` lets the caller redirect the user to a specific in-app
  // route after the magic-link sign-in completes (e.g. /accept?token=...
  // for invite flows). Encoded as `?next=...` so EmailLinkComplete can
  // honor it without colliding with Firebase's own appended params.
  const base = `${window.location.origin}${COMPLETE_PATH}`;
  const url = options.continueTo
    ? `${base}?next=${encodeURIComponent(options.continueTo)}`
    : base;
  await sendSignInLinkToEmail(auth, trimmed, {
    url,
    handleCodeInApp: true,
  });
  localStorage.setItem(PENDING_EMAIL_KEY, trimmed);
}

export function isMagicLinkUrl(auth: Auth, href: string): boolean {
  return isSignInWithEmailLink(auth, href);
}

// Completes the sign-in. If the user clicks the link on a device different
// from where they requested it, localStorage is empty — we prompt for the
// email so they can finish on the new device.
export async function completeMagicLink(
  auth: Auth,
  href: string,
  fallbackEmail?: string,
): Promise<UserCredential> {
  const email = (
    fallbackEmail?.trim() ?? localStorage.getItem(PENDING_EMAIL_KEY) ?? ""
  )
    .trim()
    .toLowerCase();
  if (!email) {
    throw new Error(
      "Necesitamos confirmar el email del enlace. Vuelve a intentarlo desde el dispositivo donde lo solicitaste o introdúcelo abajo.",
    );
  }
  const credential = await signInWithEmailLink(auth, email, href);
  localStorage.removeItem(PENDING_EMAIL_KEY);
  return credential;
}

export function getPendingEmail(): string | null {
  return localStorage.getItem(PENDING_EMAIL_KEY);
}
